<script setup>
import { HrIcon } from '../primitives';

/**
 * What a surface shows when it has nothing to show, and what to do about it.
 *
 * **Absence is not an error.** A collection nobody has filled yet, and a filter nobody's records
 * happen to match, are both ordinary states of a working screen. Drawn with a warning icon they
 * read as a fault the reader has to account for, so nothing here carries error colour and the icon
 * is the caller's to choose.
 *
 * **It says what to do next, or it is a shrug.** The one thing a reader needs from an empty screen
 * is the way off it: the control that fills the collection, or the one that undoes whatever hid its
 * contents. Those go in `actions`, and a use with none should be rare enough to justify itself.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen A collection, list or table has nothing to show and the reader needs a way forward
 * @avoidWhen Something went wrong — use HrErrorBanner, which says so in the colour for it
 * @avoidWhen The content is still arriving; a skeleton or a spinner says that, and this says the opposite
 * @related HrErrorBanner
 * @related HrInfoBanner
 * @a11y The title is a heading, so a reader moving by headings lands on the explanation rather than
 * having to find it. The icon is decorative and hidden from assistive technology, the title
 * carrying the meaning instead.
 */
defineOptions({ name: 'HrEmptyState' });

defineProps({
  /**
   * What is absent, in the reader's terms — "No clients yet". A sentence about the state, not an
   * apology for it.
   * @example No clients yet
   */
  title: {
    type: String,
    required: true,
  },
  /**
   * One line on why the surface is empty, or what would fill it. Absent when the title says enough.
   * @example Add your first client, or import them from a file.
   */
  message: {
    type: String,
    default: null,
  },
  /**
   * Material icon name. Pick one for what is missing rather than for the fact that it is: a person
   * for people, a page for documents, a magnifier for a search that found nothing.
   * @example group
   */
  icon: {
    type: String,
    default: 'inbox',
  },
});

/*
 * The legacy palette gives HrIcon no size class, so the icon falls back to the surrounding text
 * size and reads as a bullet rather than the illustration this state is built around. 48px is what
 * the xl token resolves to under a theme.
 * @todo Remove legacy-props when Legacy theme is removed
 */
const legacyIconProps = { size: '48px' };
</script>

<template>
  <div class="hr-empty-state">
    <hr-icon
      class="hr-empty-state__icon"
      :name="icon"
      size="xl"
      :legacy-props="legacyIconProps"
      aria-hidden="true"
    />
    <h2 class="hr-empty-state__title">
      {{ title }}
    </h2>
    <p
      v-if="message !== null"
      class="hr-empty-state__message"
    >
      {{ message }}
    </p>
    <div
      v-if="$slots.actions"
      class="hr-empty-state__actions"
    >
      <!-- @slot The way off this screen. One or two controls, the likeliest first. -->
      <slot name="actions" />
    </div>
  </div>
</template>

<style lang="scss" scoped>

.hr-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--app-comp-empty-state-gap);
  padding: var(--app-comp-empty-state-padding);
  text-align: center;
}

/*
 * Quieter than the words it introduces. It is there to say the surface is empty on purpose, and an
 * icon at full strength argues with the title for the one thing the reader should read first.
 */
.hr-empty-state__icon {
  color: var(--app-on-surface-variant);
  opacity: var(--app-comp-empty-state-icon-opacity);
}

.hr-empty-state__title {
  margin: 0;
  color: var(--app-on-surface);
  font-size:      var(--app-type-title-medium-size);
  line-height:    var(--app-type-title-medium-line-height);
  font-weight:    var(--app-type-title-medium-weight);
  letter-spacing: var(--app-type-title-medium-tracking);
}

.hr-empty-state__message {
  margin: 0;
  max-width: var(--app-comp-empty-state-message-width);
  color: var(--app-on-surface-variant);
  font-size:      var(--app-type-body-medium-size);
  line-height:    var(--app-type-body-medium-line-height);
  font-weight:    var(--app-type-body-medium-weight);
  letter-spacing: var(--app-type-body-medium-tracking);
}

.hr-empty-state__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--app-comp-empty-state-actions-gap);
}

</style>
